import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Package, 
  Users, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  TrendingUp,
  Search
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Dashboard component with user context
  const { data: overview, isLoading, error } = useQuery(
    'dashboard-overview',
    dashboardAPI.getOverview,
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Error loading dashboard data</div>;

  const stats = [
    {
      name: 'Total Shipments',
      value: overview?.totals?.shipments || 0,
      icon: Package,
      color: 'bg-blue-500',
    },
    {
      name: 'Total Customers',
      value: overview?.totals?.customers || 0,
      icon: Users,
      color: 'bg-green-500',
    },
    {
      name: 'Overdue Shipments',
      value: overview?.overdueShipments?.length || 0,
      icon: Clock,
      color: 'bg-red-500',
    },
    {
      name: 'Recent Shipments',
      value: overview?.recentShipments?.length || 0,
      icon: TrendingUp,
      color: 'bg-purple-500',
    },
  ];

  // Extract stats for display
  const totalShipments = overview?.totals?.shipments || 0;
  const totalCustomers = overview?.totals?.customers || 0;
  const delivered = overview?.totals?.delivered || 0;
  const overdue = overview?.overdueShipments?.length || 0;
  const recentActivity = overview?.recentShipments?.map(shipment => ({
    shipment_id: shipment.id,
    action: `Status: ${shipment.shipment_status}`,
    created_at: shipment.created_at,
    customer_name: shipment.customer_name,
    city_name: shipment.city_name
  })) || [];

  const handleQuickAction = (action) => {
    switch (action) {
      case 'new-shipment':
        navigate('/shipments');
        break;
      case 'add-customer':
        navigate('/customers');
        break;
      case 'inquiry':
        navigate('/shipments?status=inquiry');
        break;
      case 'view-reports':
        // Could navigate to a reports page when implemented
        break;
      default:
        break;
    }
  };

  const handleShipmentClick = (shipmentId) => {
    navigate(`/shipments/${shipmentId}`);
  };

  return (
    <div className="space-y-3 lg:space-y-4 xl:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 lg:gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm lg:text-base text-gray-600">Welcome back, {user?.full_name}</p>
        </div>
        <div className="flex items-center space-x-2 lg:space-x-3">
          <span className="text-sm lg:text-base text-gray-500">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white rounded-lg shadow p-4 lg:p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Package className="h-6 w-6 lg:h-8 lg:w-8 text-blue-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-sm lg:text-base font-medium text-gray-600">Total Shipments</p>
              <p className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">{overview?.totals?.shipments || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 lg:p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-6 w-6 lg:h-8 lg:w-8 text-green-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-sm lg:text-base font-medium text-gray-600">Total Customers</p>
              <p className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">{overview?.totals?.customers || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 lg:p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-6 w-6 lg:h-8 lg:w-8 text-green-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-sm lg:text-base font-medium text-gray-600">Delivered</p>
              <p className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">{overview?.totals?.delivered || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 lg:p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 lg:h-8 lg:w-8 text-yellow-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-sm lg:text-base font-medium text-gray-600">Overdue</p>
              <p className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">{overview?.overdueShipments?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900">Quick Actions</h2>
        </div>
        <div className="p-4 lg:p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <button 
              className="flex flex-col items-center justify-center p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-colors"
              onClick={() => handleQuickAction('new-shipment')}
            >
              <Package className="h-5 w-5 lg:h-6 lg:w-6 mb-1 lg:mb-2" />
              <span className="text-xs lg:text-sm text-center">New Shipment</span>
            </button>
            <button 
              className="flex flex-col items-center justify-center p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-colors"
              onClick={() => handleQuickAction('add-customer')}
            >
              <Users className="h-5 w-5 lg:h-6 lg:w-6 mb-1 lg:mb-2" />
              <span className="text-xs lg:text-sm text-center">Add Customer</span>
            </button>
            <button 
              className="flex flex-col items-center justify-center p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-colors"
              onClick={() => handleQuickAction('inquiry')}
            >
              <Search className="h-5 w-5 lg:h-6 lg:w-6 mb-1 lg:mb-2" />
              <span className="text-xs lg:text-sm text-center">Inquiry</span>
            </button>
            <button 
              className="flex flex-col items-center justify-center p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-colors"
              onClick={() => handleQuickAction('view-reports')}
            >
              <TrendingUp className="h-5 w-5 lg:h-6 lg:w-6 mb-1 lg:mb-2" />
              <span className="text-xs lg:text-sm text-center">View Reports</span>
            </button>
          </div>
        </div>
      </div>

      {/* Recent Activity and Overdue Shipments */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 lg:gap-4 xl:gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200">
            <h2 className="text-lg lg:text-xl font-semibold text-gray-900">Recent Activity</h2>
          </div>
          <div className="p-4 lg:p-6">
            {recentActivity.length > 0 ? (
              <div className="space-y-3 lg:space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 lg:space-x-4 p-3 lg:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer" onClick={() => handleShipmentClick(activity.shipment_id)}>
                    <div className="flex-shrink-0 w-8 h-8 lg:w-10 lg:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Package className="h-4 w-4 lg:h-5 lg:w-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm lg:text-base font-medium text-gray-900">
                        Shipment #{activity.shipment_id} - {activity.customer_name}
                      </p>
                      <p className="text-xs lg:text-sm text-gray-600">
                        {activity.city_name} • {activity.action} • {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 lg:py-12">
                <Package className="h-12 w-12 lg:h-16 lg:w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg lg:text-xl font-medium text-gray-900 mb-2">No recent activity</h3>
                <p className="text-sm lg:text-base text-gray-600">Start by creating your first shipment</p>
              </div>
            )}
          </div>
        </div>

        {/* Overdue Shipments */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200">
            <h2 className="text-lg lg:text-xl font-semibold text-gray-900">Overdue Shipments</h2>
          </div>
          <div className="p-4 lg:p-6">
            {overview?.overdueShipments?.length > 0 ? (
              <div className="space-y-3 lg:space-y-4">
                {overview.overdueShipments.map((shipment, index) => (
                  <div key={index} className="flex items-start space-x-3 lg:space-x-4 p-3 lg:p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer" onClick={() => handleShipmentClick(shipment.id)}>
                    <div className="flex-shrink-0 w-8 h-8 lg:w-10 lg:h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="h-4 w-4 lg:h-5 lg:w-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm lg:text-base font-medium text-gray-900">
                        Shipment #{shipment.id} - {shipment.customer_name}
                      </p>
                      <p className="text-xs lg:text-sm text-gray-600">
                        {shipment.city_name} • Due: {new Date(shipment.due_date).toLocaleDateString()} • Status: {shipment.shipment_status}
                      </p>
                      <p className="text-xs text-red-600 font-medium">
                        {Math.ceil((new Date() - new Date(shipment.due_date)) / (1000 * 60 * 60 * 24))} days overdue
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 lg:py-12">
                <CheckCircle className="h-12 w-12 lg:h-16 lg:w-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg lg:text-xl font-medium text-gray-900 mb-2">No overdue shipments</h3>
                <p className="text-sm lg:text-base text-gray-600">All shipments are on track!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 